import { useEffect, useId, useRef, useState } from 'react';
import { Image as ImageIcon, LoaderCircle, Trash2, Upload } from 'lucide-react';
import type { CharacterUgcConfig, CharacterUgcRole } from '../services/characterUgc';

interface CharacterUgcProfilesProps {
  config: CharacterUgcConfig;
  readOnlyProfile?: boolean;
  onProfileChange?: (role: CharacterUgcRole, field: 'name' | 'appearance' | 'voice', value: string) => void;
  onPortraitChange: (role: CharacterUgcRole, file: File | null) => Promise<void>;
}

const FIELDS = [
  { field: 'name', label: 'Name', maxLength: 80 },
  { field: 'appearance', label: 'Appearance', maxLength: 600 },
  { field: 'voice', label: 'Voice', maxLength: 200 },
] as const;

export default function CharacterUgcProfiles({ config, readOnlyProfile = false, onProfileChange, onPortraitChange }: CharacterUgcProfilesProps) {
  const formId = useId();
  const inputs = useRef<Partial<Record<CharacterUgcRole, HTMLInputElement | null>>>({});
  const pending = useRef(new Set<CharacterUgcRole>());
  const mountedRef = useRef(true);
  const [busyRoles, setBusyRoles] = useState<CharacterUgcRole[]>([]);
  const [errors, setErrors] = useState<Partial<Record<CharacterUgcRole, string>>>({});

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const changePortrait = async (role: CharacterUgcRole, file: File | null) => {
    if (!mountedRef.current || pending.current.has(role)) return;
    pending.current.add(role);
    setBusyRoles([...pending.current]);
    setErrors(current => ({ ...current, [role]: '' }));
    try {
      await onPortraitChange(role, file);
    } catch (error) {
      if (mountedRef.current) setErrors(current => ({ ...current, [role]: error instanceof Error ? error.message : 'Unable to attach this portrait.' }));
    } finally {
      pending.current.delete(role);
      if (mountedRef.current) setBusyRoles([...pending.current]);
    }
  };

  return (
    <div className={`grid min-w-0 grid-cols-1 gap-4 ${readOnlyProfile ? '' : 'sm:grid-cols-2'}`}>
      {config.characters.map(character => {
        const roleLabel = character.role === 'wife' ? 'Wife' : 'Husband';
        const busy = busyRoles.includes(character.role);
        const uploadLabel = readOnlyProfile ? `Reattach original ${roleLabel} portrait` : `Upload ${roleLabel} portrait`;
        return (
          <fieldset key={character.role} aria-label={`${roleLabel} profile`} className="min-w-0 space-y-2">
            <legend className="mb-2 text-xs font-bold text-[#865916]">{roleLabel}</legend>
            <div className="flex min-w-0 items-start gap-2">
              {character.imageUrl ? (
                <img src={character.imageUrl} alt={`${roleLabel} portrait`} width={64} height={64} className="h-16 w-16 shrink-0 rounded-md border border-[#d6bd7c] object-cover" />
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-dashed border-[#d6bd7c] text-[#a89470]">
                  <ImageIcon className="h-5 w-5" aria-hidden="true" />
                </div>
              )}
              <div className="min-w-0 flex-1 space-y-1">
                <span className="block break-all text-[10px] text-[#6d5b3f]">{character.imageFile || 'No portrait'}</span>
                {!character.imageUrl && <p role="status" className="text-[10px] text-[#8c2d1f]">{readOnlyProfile ? 'Original portrait missing' : 'Portrait required'}</p>}
                <div className="flex gap-1">
                  <button type="button" aria-label={uploadLabel} title={uploadLabel} disabled={busy} aria-busy={busy}
                    onClick={() => inputs.current[character.role]?.click()}
                    className="flex h-8 w-8 items-center justify-center rounded border border-[#d6bd7c] text-[#865916] hover:bg-[#e9d6aa]/60 disabled:cursor-not-allowed disabled:opacity-50">
                    {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  </button>
                  <button type="button" aria-label={`Remove ${roleLabel} portrait`} title={`Remove ${roleLabel} portrait`} disabled={busy || !character.imageUrl}
                    onClick={() => void changePortrait(character.role, null)}
                    className="flex h-8 w-8 items-center justify-center rounded border border-[#d6bd7c] text-[#8c2d1f] hover:bg-[#dc553c]/10 disabled:cursor-not-allowed disabled:opacity-50">
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <input type="file" accept="image/png,image/jpeg,image/webp" aria-label={`${roleLabel} portrait file`} className="hidden"
                    ref={input => { inputs.current[character.role] = input; }}
                    onChange={async event => {
                      const file = event.currentTarget.files?.[0];
                      event.currentTarget.value = '';
                      if (file) await changePortrait(character.role, file);
                    }} />
                </div>
              </div>
            </div>
            {errors[character.role] && <p role="alert" className="break-words text-[11px] text-[#8c2d1f]">{errors[character.role]}</p>}
            {FIELDS.map(({ field, label, maxLength }) => {
              const id = `${formId}-${character.role}-${field}`;
              const shared = {
                id, 'aria-label': `${roleLabel} ${field}`, value: character[field], maxLength,
                readOnly: readOnlyProfile,
                onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onProfileChange?.(character.role, field, event.target.value),
                className: 'w-full min-w-0 rounded-md border border-[#d6bd7c] bg-[#fff8ec] px-2 py-1.5 text-xs text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]/50',
              };
              return (
                <div key={field} className="min-w-0 space-y-1">
                  <label htmlFor={id} className="block text-[10px] font-bold text-[#6d5b3f]">{label}</label>
                  {field === 'appearance' ? <textarea {...shared} rows={2} className={`${shared.className} resize-y`} /> : <input {...shared} type="text" />}
                </div>
              );
            })}
          </fieldset>
        );
      })}
    </div>
  );
}